import {
  Component,
  Output,
  EventEmitter,
  Input,
  ViewChild,
  ElementRef,
  OnInit,
} from "@angular/core";
import { TaskDataService } from "../../../shared/services/task-data.service";
import { ITaskRequest, TaskRequest } from "../../../modules/tasks/model/types";
import notify from "devextreme/ui/notify";
import validationEngine from "devextreme/ui/validation_engine";

import {
  getNotificationTimeout,
  sendNotificationMessage,
} from "../../../types/resource";
import { LoggingService } from "../../../framework/logging/logging.service";
import { SeverityLevel } from "../../../framework/logging/severity-level.model";
import { BehaviorSubject, lastValueFrom } from "rxjs";
import { filter, take } from "rxjs/operators";
import { MessageService } from "../../../shared/components/framework/services/message.service";
import { StaticDataService } from "../../../shared/services/static-data.service";
import { FormUtils } from "../../../shared/components/framework/services/form-builder.service";
import { DataGridSelectionService } from "../../../shared/services/data-grid-selection.service";
import { LoanDataBase } from "../baseclass/loandata-base";

@Component({
  selector: "app-service-request",
  templateUrl: "./service-request.component.html",
  styleUrl: "./service-request.component.scss",
})
export class ServiceRequestComponent extends LoanDataBase implements OnInit {
  @ViewChild("formElement") formElement!: ElementRef;

  @Input() taskTemplateName: string;
  @Input() task: ITaskRequest;

  @Output() onAddNewTaskEventEmitter: EventEmitter<any> = new EventEmitter();

  requestTypes: any[];
  bankData: any[];
  requestData: any = {};
  billableToClientData: any[];
  isLoading: boolean = false;
  businessDayCalendars: any[];
  files: File[] = [];
  directors: any[];
  seniorDirectors: any[];
  today: Date = new Date();
  isUrgent: boolean = false;
  formItemsBehaviourSubject$ = new BehaviorSubject<any[]>([]);
  formItems$ = this.formItemsBehaviourSubject$.asObservable();
  formItems: any[] = [];
  formColCountBehaviourSubject$ = new BehaviorSubject<number>(2);
  formColCount$ = this.formColCountBehaviourSubject$.asObservable();
  formColCount: number = 2;
  mandatoryFiles: boolean = false;
  feeTypes: any[] = [];
  isEditable: boolean = false;
  currentRequestType: string = "";
  override showInactiveLoans: boolean = false;
  showInactiveDeals: boolean = false;
  isUpdatingLoanData: boolean = false;
  isUpdatingDealData: boolean = false;
  isGridInEditMode: boolean = false;
  currentFormInstance: any = null;
  shouldDisableCheckbox: boolean = false;
  checkboxWidget: any = null;
  selectedDealLoanNumbers: string[] = [];

  private doNotExcludeSelectedLoans: boolean = false;

  constructor(
    taskDataService: TaskDataService,
    private readonly staticDataService: StaticDataService,
    private readonly loggingService: LoggingService,
    private readonly messageService: MessageService,
    private readonly selectionService: DataGridSelectionService,
  ) {
    super(taskDataService);
    this.onRequestTypeSelectionChanged =
      this.onRequestTypeSelectionChanged.bind(this);
    this.onDealNumberSelectionChanged =
      this.onDealNumberSelectionChanged.bind(this);
    this.formItems$.subscribe((items: any[]) => {
      this.formItems = items;
    });

    this.formColCount$.subscribe((colcount: number) => {
      this.formColCount = colcount;
    });

    // Subscribe to loan data changes and refresh the loan selectbox
    this.loanNumbers$.subscribe((data) => {
      this.loanNumbers = data;
      if (this.currentFormInstance) {
        const loanEditor = this.currentFormInstance.getEditor('loanNumber');
        if (loanEditor) {
          loanEditor.option('items', this.loanNumbers);
        }
      }
    });

    // Subscribe to deal data changes to update loan numbers display when editing
    this.dealData$.subscribe((data) => {

      // Note: The base class also subscribes and sets this.dealData
      // We don't set it here to avoid duplication, but we need to process changes

      // Skip only if we haven't loaded any data yet (not even allDealData)
      // This handles the initial empty BehaviorSubject emission
      if ((!data || data.length === 0) && (!this.allDealData || this.allDealData.length === 0)) {
        return;
      }

      // If filtered data is empty but we have all data, use all data
      const dataToUse = (data && data.length > 0) ? data : (this.allDealData || []);

      if (this.isEditable && this.requestData && this.requestData.dealNumber && dataToUse.length > 0) {
        const selectedDeal = dataToUse.find(deal => deal.name === this.requestData.dealNumber);
        if (selectedDeal && selectedDeal.loanNumbers) {
          this.selectedDealLoanNumbers = selectedDeal.loanNumbers;
          this.requestData.selectedDealLoanNumbers = this.selectedDealLoanNumbers;
        }
      }

      // Update form configuration if it's already been created
      if (this.currentRequestType && this.formItems.length > 0) {
        this.updateDealDataInFormConfig();
        this.updateDealEditorItems(dataToUse);
      } else if (this.currentRequestType) {
        // Form not created yet, but we have a request type - just update the editor when it becomes available
        setTimeout(() => this.updateDealEditorItems(dataToUse), 100);
      }
    });
  }

  onFileUpload(file: File) {
    this.files.push(file);
  }

  onFileRemove(file: File) {
    if (file != null && file != undefined) {
      this.files = this.files.filter((f) => f !== file);
    }
  }

  onFormInitialized(e: any): void {
    this.currentFormInstance = e.component;
    // If deal data has already loaded, update the deal editor immediately
    // Use all data if filtered data is empty
    const dealDataToUse = (this.dealData && this.dealData.length > 0) ? this.dealData : this.allDealData;
    if (dealDataToUse && dealDataToUse.length > 0) {
      setTimeout(() => {
        this.updateDealEditorItems(dealDataToUse);
      }, 0);
    }

    // If loan data has already loaded, update the loan editor immediately
    if (this.loanNumbers && this.loanNumbers.length > 0) {
      setTimeout(() => {
        const loanNumberEditor = this.currentFormInstance?.getEditor('loanNumber');
        if (loanNumberEditor) {
          loanNumberEditor.option('items', this.loanNumbers);
          loanNumberEditor.repaint();
        }
      }, 0);
    }
  }

  override onShowInactiveLoansChanged(e: any): void {
    this.showInactiveLoans = e.value;
    this.isUpdatingLoanData = true;
    super.onShowInactiveLoansChanged(e);

    // For LOAN ADVANCE, don't update the form config as it destroys the data grid
    // The loan data change will be picked up by the lookup in the grid automatically
     if (this.currentRequestType !== "LOAN ADVANCE" && this.currentRequestType !== "AUDIT CONFIRM") {
      // Reset flag after a short delay to allow the subscription to process
      setTimeout(() => {
        this.isUpdatingLoanData = false;
      }, 50);
    } else {
      // For LOAN ADVANCE, just reset the flag immediately without form update
      this.isUpdatingLoanData = false;
    }
  }

  override onShowInactiveDealsChanged(e: any): void {
    this.showInactiveDeals = e.value;
    this.isUpdatingDealData = true;



    super.onShowInactiveDealsChanged(e);

    // For EXTENSION requests, handle the form update differently
    if (this.currentRequestType === "EXTENSION" || this.currentRequestType === "Extension") {

      // Since the subscription updates this.dealData immediately, let's use a Promise-based approach
      this.dealData$.pipe(
        filter((data: any) => data && data.length > 0),
        take(1)
      ).subscribe((data: any) => {
        // Try to update the dropdown directly through the form instance
        if (this.currentFormInstance) {
          const dealNumberEditor = this.currentFormInstance.getEditor('dealNumber');
          if (dealNumberEditor) {
            dealNumberEditor.option('items', data);
            dealNumberEditor.repaint();
          } else {
            // Fallback to form recreation
            this.onRequestTypeSelectionChanged({
              value: this.currentRequestType,
              selectedItem: { name: this.currentRequestType }
            });
          }
        } else {
          // Fallback to form recreation
          this.onRequestTypeSelectionChanged({
            value: this.currentRequestType,
            selectedItem: { name: this.currentRequestType }
          });
        }
        this.isUpdatingDealData = false;
      });
    } else if (this.currentRequestType !== "LOAN ADVANCE" && this.currentRequestType !== "AUDIT CONFIRM") {
      // Reset flag after a short delay to allow the subscription to process
      setTimeout(() => {
        this.isUpdatingDealData = false;
      }, 50);
    } else {
      // For LOAN ADVANCE and AUDIT CONFIRM, just reset the flag immediately without form update
      this.isUpdatingDealData = false;
    }
  }

  onDealNumberSelectionChanged(e: any): void {
    const selectedDealName = e.value;

    if (selectedDealName && this.dealData) {
      const selectedDeal = this.dealData.find(deal => deal.name === selectedDealName);
      if (selectedDeal && selectedDeal.loanNumbers) {
        this.selectedDealLoanNumbers = selectedDeal.loanNumbers;
      } else {
        this.selectedDealLoanNumbers = [];
      }
    } else {
      this.selectedDealLoanNumbers = [];
    }

    // Update the form data
    if (this.requestData) {
      this.requestData.selectedDealLoanNumbers = this.selectedDealLoanNumbers;

      // Update the selectedDealLoanNumbers field in the form for display
      if (this.currentFormInstance) {
        // Format loan numbers for display
        let loanNumbersDisplay = '';
        if (Array.isArray(this.selectedDealLoanNumbers)) {
          const loanNumberStrings = this.selectedDealLoanNumbers.map((ln: any) => {
            return typeof ln === 'string' ? ln : (ln?.name || ln?.toString() || String(ln));
          });
          loanNumbersDisplay = loanNumberStrings.join(', ');
        }

        // Update the selectedDealLoanNumbers field for display
        this.requestData.selectedDealLoanNumbers = loanNumbersDisplay;
        this.currentFormInstance.updateData('selectedDealLoanNumbers', loanNumbersDisplay);

        // Also update the editor directly
        setTimeout(() => {
          const selectedLoanNumbersEditor = this.currentFormInstance.getEditor('selectedDealLoanNumbers');
          if (selectedLoanNumbersEditor) {
            selectedLoanNumbersEditor.option('value', loanNumbersDisplay);
            selectedLoanNumbersEditor.repaint();
          }
        }, 0);
      } else {
        // Format loan numbers for display
        let loanNumbersDisplay = '';
        if (Array.isArray(this.selectedDealLoanNumbers)) {
          const loanNumberStrings = this.selectedDealLoanNumbers.map((ln: any) => {
            return typeof ln === 'string' ? ln : (ln?.name || ln?.toString() || String(ln));
          });
          loanNumbersDisplay = loanNumberStrings.join(', ');
        }
        this.requestData.selectedDealLoanNumbers = loanNumbersDisplay;
      }
    } else {
    }
  }

  get loanDataForGrid(): any[] {
    const requestType = this.currentRequestType?.toUpperCase();
    if (requestType === "LOAN ADVANCE") {
      return this.allLoanData || [];
    }

    return this.allLoanNumbers || [];
  }

  onGridShowInactiveLoansChange(checked: boolean): void {
    this.showInactiveLoans = checked;
    this.updateCheckboxDisabledState();
  }

  onGridEditModeChange(inEditMode: boolean): void {
    this.isGridInEditMode = inEditMode;
    this.updateCheckboxDisabledState();
  }

  onGridLoanAmountsChange(loanAmounts: any[]): void {
    this.requestData.loanAmounts = loanAmounts;
    this.updateCheckboxDisabledState();

    const loanAmountsProxyEditor = this.currentFormInstance?.getEditor("loanAmountsProxy");
    if (loanAmountsProxyEditor) {
      loanAmountsProxyEditor.option(
        "value",
        Array.isArray(this.requestData.loanAmounts)
          ? this.requestData.loanAmounts.length.toString()
          : "0",
      );
    }
  }

  private updateCheckboxDisabledState(): void {
    const hasItems = this.requestData.loanAmounts && this.requestData.loanAmounts.length > 0;
    this.shouldDisableCheckbox = this.isGridInEditMode || hasItems;

    // Update checkbox in form configuration for non-LOAN ADVANCE forms
    if (
      this.formItems &&
      this.formItems.length > 0 &&
      this.currentRequestType !== "LOAN ADVANCE" && this.currentRequestType.toUpperCase() !== "AUDIT CONFIRM"
    ) {
      const updated = this.updateCheckboxInFormItems(
        this.formItems,
        this.shouldDisableCheckbox,
      );
      if (updated) {
        this.formItemsBehaviourSubject$.next([...this.formItems]);
      }
    }
  }

  private updateCheckboxInFormItems(items: any[], disabled: boolean): boolean {
    for (const item of items) {
      // Check if this is the show inactive loans checkbox (by CSS class and editor type)
      if (
        item.editorType === "dxCheckBox" &&
        item.cssClass === "loan-checkbox-center-align"
      ) {
        if (!item.editorOptions) {
          item.editorOptions = {};
        }

        // Preserve the current value from the component property
        const currentValue = this.showInactiveLoans;

        // Update the disabled state while preserving the value
        item.editorOptions.disabled = disabled;

        // Make sure the value is properly set in the editor options
        item.editorOptions.value = currentValue;

        return true; // Found and updated
      }

      // Check nested items recursively
      if (item.itemType === "group" && item.items) {
        if (this.updateCheckboxInFormItems(item.items, disabled)) {
          return true;
        }
      }

      if (item.items && Array.isArray(item.items)) {
        if (this.updateCheckboxInFormItems(item.items, disabled)) {
          return true;
        }
      }
    }
    return false; // Not found
  }

  private updateLoanDataInFormConfig(): void {
    if (!this.currentFormInstance) {
      return;
    }
    const loanEditor = this.currentFormInstance.getEditor('loanNumber');
    if (loanEditor) {
      loanEditor.option('items', this.loanNumbers);
    }

    // Update in place so function references (validationCallback) are preserved
    this.updateLoanDataRecursively(this.formItems);

    // Emit a new array reference for change detection
    this.formItemsBehaviourSubject$.next([...this.formItems]);
  }

    private updateDealDataInFormConfig(): void {
    if (!this.formItems || this.formItems.length === 0) {
      return;
    }

    // Update in place so function references (validationCallback) are preserved
    this.updateDealDataRecursively(this.formItems);

    // Emit a new array reference for change detection
    this.formItemsBehaviourSubject$.next([...this.formItems]);
  }

  private updateLoanDataRecursively(items: any[]): void {
    for (const item of items) {
      // Check if this is a loan select box
      if (
        item.dataField === "loanNumber" &&
        item.editorType === "dxSelectBox"
      ) {
        // Update only the items, preserve all other editorOptions including itemTemplate
        if (item.editorOptions) {
          item.editorOptions.items = this.loanNumbers;
          // Ensure itemTemplate is preserved
          if (!item.editorOptions.itemTemplate) {
            item.editorOptions.itemTemplate = this.loanItemTemplate;
          }
        }
      }

      // Check if this is a checkbox for showing inactive loans
      if (
        item.editorType === "dxCheckBox" &&
        item.cssClass === "loan-checkbox-center-align"
      ) {
        if (item.editorOptions) {
          item.editorOptions.value = this.showInactiveLoans;
          item.editorOptions.text = this.activeLoanCheckboxText;
          // Restore the function reference that was lost during JSON serialization
          item.editorOptions.onValueChanged = this.onShowInactiveLoansChanged;
        }
      }

      // Check if this is a group with items
      if (item.itemType === "group" && item.items) {
        this.updateLoanDataRecursively(item.items);
      }

      // Also check for nested items in other structures
      if (item.items && Array.isArray(item.items)) {
        this.updateLoanDataRecursively(item.items);
      }
    }
  }

    private updateDealDataRecursively(items: any[]): void {
    for (const item of items) {
      // Check if this is a deal select box
      if (
        item.dataField === "dealNumber" &&
        item.editorType === "dxSelectBox"
      ) {
        // Update only the items, preserve all other editorOptions including itemTemplate
        // Use all data if filtered data is empty (all deals are inactive)
        const dealDataToUse = (this.dealData && this.dealData.length > 0) ? this.dealData : this.allDealData;
        if (item.editorOptions) {
          item.editorOptions.items = dealDataToUse;
          // Ensure itemTemplate is preserved
          if (!item.editorOptions.itemTemplate) {
            item.editorOptions.itemTemplate = this.dealItemTemplate;
          }
          // Ensure onValueChanged is preserved
          if (!item.editorOptions.onValueChanged) {
            item.editorOptions.onValueChanged = this.onDealNumberSelectionChanged;
          }
        }
      }

      // Check if this is a checkbox for showing inactive deals
      if (
        item.editorType === "dxCheckBox" &&
        item.cssClass === "deal-checkbox-center-align"
      ) {
        if (item.editorOptions) {
          item.editorOptions.value = this.showInactiveDeals;
          item.editorOptions.text = this.activeDealCheckboxText;
          // Restore the function reference that was lost during JSON serialization
          item.editorOptions.onValueChanged = this.onShowInactiveDealsChanged;
        }
      }

      // Check if this is a group with items
      if (item.itemType === "group" && item.items) {
        this.updateDealDataRecursively(item.items);
      }

      // Also check for nested items in other structures
      if (item.items && Array.isArray(item.items)) {
        this.updateDealDataRecursively(item.items);
      }
    }
  }

  private updateDealEditorItems(data: any[]): void {

    if (this.currentFormInstance) {
      const dealNumberEditor = this.currentFormInstance.getEditor('dealNumber');

      if (dealNumberEditor) {
        dealNumberEditor.option('items', data || []);
        dealNumberEditor.repaint();
      } else {
        // Form instance exists but editor not found yet, retry after delays
        setTimeout(() => {
          const retryEditor = this.currentFormInstance?.getEditor('dealNumber');
          if (retryEditor) {
            retryEditor.option('items', data || []);
            retryEditor.repaint();
          }
        }, 100);

        setTimeout(() => {
          const retryEditor2 = this.currentFormInstance?.getEditor('dealNumber');
          if (retryEditor2) {
            retryEditor2.option('items', data || []);
            retryEditor2.repaint();
          }
        }, 300);
      }
    }
  }

  onRequestTypeSelectionChanged(e: any) {
    this.currentRequestType = e.selectedItem.name;
    const requestTypeNameUpper = e.selectedItem.name?.toUpperCase?.() ?? "";

    let config = [];
    switch (requestTypeNameUpper) {
      case "ESCROW / RESERVE - TREASURY RECON":
        this.mandatoryFiles = !this.isEditable;
        config = [
          {
            dataField: "requestType",
            editorType: "dxTextBox",
            label: { text: "Request Type" },
            colSpan: 2,
            cssClass: "dx-widget",
            editorOptions: { value: e.value, readOnly: true, required: "true" },
            validationRules: [
              { type: "required", message: "Request Type is required" },
            ],
          },
          {
            dataField: "description",
            editorType: "dxTextArea",
            label: { text: "Description" },
            colSpan: 2,
            cssClass: "dx-widget",
            editorOptions: {
              required: "true",
              defaultValue: "",
              height: 100,
            },
            validationRules: [
              { type: "required", message: "Description is required" },
            ],
          },

          {
            colSpan: 2,
            horizontalAlignment: "center",
            itemType: "simple",
            name: "fileUpload",
            label: {
              text: this.mandatoryFiles ? "Upload Files *" : "Upload Files",
            },
            template: "fileUploadTemplate",
          },
          {
            // work around to bind file upload component to form and validate it
            dataField: "fileUploadProxy",
            colSpan: 2,
            itemType: "simple",
            label: { visible: false },
            editorType: "dxTextBox",
            editorOptions: {
              visible: false,
              value: this.files.length.toString(),
            },
            validationRules: [
              {
                type: "custom",
                message: "At least one file is required",
                validationCallback: (e) => this.onValidateFileAttachments(e),
                reevaluate: true,
              },
            ],
          },
          {
            colSpan: 2,
            horizontalAlignment: "center",
            cssClass: "borrow-submit",
            editorType: "dxButton",
            editorOptions: { text: "Submit", useSubmitBehavior: true },
          },
        ];
        break;
      default:
        this.mandatoryFiles = !this.isEditable;
        config = [
          {
            dataField: "requestType",
            editorType: "dxTextBox",
            label: { text: "Request Type" },
            colSpan: 2,
            cssClass: "dx-widget",
            editorOptions: { value: e.value, readOnly: true, required: "true" },
            validationRules: [
              { type: "required", message: "Request Type is required" },
            ],
          },
          {
            itemType: "group",
            colCount: 4,
            colSpan: 2,
            items: [
              {
                labelMode: "static",
                dataField: "loanNumber",
                editorType: "dxSelectBox",
                colSpan: 3,
                cssClass: "dx-widget",
                label: { text: "Loan Number" },
                editorOptions: {
                  items: this.loanNumbers,
                  valueExpr: "name",
                  displayExpr: "name",
                  required: true,
                  searchMode: "contains",
                  searchEnabled: true,
                  itemTemplate: this.loanItemTemplate,
                },
                validationRules: [
                  { type: "required", message: "Loan Number is required" },
                ],
              },
              {
                editorType: "dxCheckBox",
                label: { visible: false },
                cssClass: "loan-checkbox-center-align",
                editorOptions: {
                  text: this.activeLoanCheckboxText,
                  onValueChanged: this.onShowInactiveLoansChanged,
                  value: this.showInactiveLoans,
                },
              },
            ],
          },
          {
            dataField: "description",
            editorType: "dxTextArea",
            label: { text: "Description" },
            colSpan: 2,
            cssClass: "dx-widget",
            editorOptions: { required: "true", defaultValue: "", height: 100 },
            validationRules: [
              { type: "required", message: "Description is required" },
            ],
          },
          {
            colSpan: 2,
            horizontalAlignment: "center",
            itemType: "simple",
            name: "fileUpload",
            label: {
              text: this.mandatoryFiles ? "Upload Files *" : "Upload Files",
            },
            template: "fileUploadTemplate",
          },
          {
            // work around to bind file upload component to form and validate it
            dataField: "fileUploadProxy",
            colSpan: 2,
            itemType: "simple",
            label: { visible: false },
            editorType: "dxTextBox",
            editorOptions: {
              visible: false,
              value: this.files.length.toString(),
            },
            validationRules: [
              {
                type: "custom",
                message: "At least one file is required",
                validationCallback: (e) => this.onValidateFileAttachments(e),
                reevaluate: true,
              },
            ],
          },
          {
            colSpan: 2,
            horizontalAlignment: "center",
            cssClass: "borrow-submit",
            editorType: "dxButton",
            editorOptions: { text: "Submit", useSubmitBehavior: true },
          },
        ];
    }

    const dealDataToUse = (this.dealData && this.dealData.length > 0) ? this.dealData : this.allDealData;
    this.formItemsBehaviourSubject$.next(config);

    // If we have deal data (or all deal data), update the editor after a short delay to ensure form is rendered
    if (dealDataToUse && dealDataToUse.length > 0) {
      setTimeout(() => {
        this.updateDealEditorItems(dealDataToUse);
      }, 50);
    }
  }

  // Add this method to generate unique IDs
  private generateUniqueId(): string {
    return Date.now().toString() + Math.random().toString(36).substr(2, 9);
  }

  getLoanData() {
    if (this.requestData.loanAmounts && this.requestData.loanAmounts.length > 0) {
      // Search in allLoanData to find loan info regardless of active/inactive status
      const firstLoan = this.allLoanData.find((ld) => ld.name === this.requestData.loanAmounts[0].loanNumber);
      // If firstLoan is not found, return all loan data (fallback)
      if (!firstLoan) {
        console.warn('First loan not found in loan data, returning all loans');
        return this.loanNumbers;
      }

      // Extract deal identifier from loan name (first 6 digits of the loan number)
      // Format: "240002002 - Xtreme-DDTL" -> extract "240002"
      const extractDealId = (loanName: string): string | null => {
        if (!loanName) return null;
        // Extract just the loan number part (before the " - ")
        const loanNumberPart = loanName.split(' - ')[0];
        // Return first 6 digits
        return loanNumberPart.length >= 6 ? loanNumberPart.substring(0, 6) : null;
      };

      const firstLoanDealId = extractDealId(firstLoan.name);

      if (!firstLoanDealId) {
        console.warn('Could not extract deal ID from loan name, returning all loans');
        return this.loanNumbers;
      }

      const existingLoanNumbers = this.requestData.loanAmounts.map((item) => item.loanNumber);

      // Filter loans that have the same deal ID (same first 6 digits)
      const filteredLoans = this.loanNumbers.filter((l) => {
        const loanDealId = extractDealId(l.name);
        return loanDealId === firstLoanDealId;
      });

      if (this.doNotExcludeSelectedLoans) {
        //first pass if editing
        this.doNotExcludeSelectedLoans = false;
        return filteredLoans;
      }

      const finalLoans = filteredLoans.filter((l) => !existingLoanNumbers.includes(l.name));
      return finalLoans;
    }

    // If no loan amounts but we have selectedDealLoanNumbers, filter by those
    if (this.selectedDealLoanNumbers && this.selectedDealLoanNumbers.length > 0) {
      return this.loanNumbers.filter((l) => this.selectedDealLoanNumbers.includes(l.name));
    }

    return this.loanNumbers;
  }

  onValidateFileAttachments(e: any): boolean {
    if (this.mandatoryFiles && this.files.length == 0) {
      e.isValid = false;
      return false;
    }
    e.isValid = true;
    return true;
  }

  removeProxyData(data: string): string {
    let json = JSON.parse(data);
    delete json["fileUploadProxy"];
    delete json["loanAmountsProxy"];
    return JSON.stringify(json);
  }

  async onSubmit(e: any) {
    e.preventDefault();

    let validationResult = validationEngine.validateGroup("formGroup");

    if (validationResult.isValid) {
      try {
        let request: ITaskRequest = new TaskRequest();
        let requestType = this.requestTypes.find((t) => t.name == this.requestData.requestType);
        request.templateId = requestType.id;

        request.name = requestType.name;
        if (this.requestData.loanAmounts != null && this.requestData.loanAmounts.length > 0) {
          request.referenceName = `${this.requestData.loanAmounts[0].loanNumber}`;
        } else {
          request.referenceName = this.requestData.loanNumber || this.requestData.dealNumber || "";
          this.requestData.loanNumber = this.requestData.loanNumber || "";
        }

        request.referenceType = requestType.referenceType;

        // Create a copy of requestData to modify for submission
        let submissionData = { ...this.requestData };

        // Convert selectedDealLoanNumbers back to array if it's a string (for display purposes)
        if (submissionData.selectedDealLoanNumbers && typeof submissionData.selectedDealLoanNumbers === 'string') {
          // Convert comma-separated string back to array, filtering out empty strings
          submissionData.selectedDealLoanNumbers = submissionData.selectedDealLoanNumbers
            .split(',')
            .map(ln => ln.trim())
            .filter(ln => ln.length > 0);
        } else if (!submissionData.selectedDealLoanNumbers && this.selectedDealLoanNumbers) {
          // Fallback to the original array if the form field is empty but we have the original data
          submissionData.selectedDealLoanNumbers = this.selectedDealLoanNumbers;
        }

        // If selectedDealLoanNumbers is present, remove loanNumber property and rename to loanNumbers
        if (submissionData.selectedDealLoanNumbers && Array.isArray(submissionData.selectedDealLoanNumbers) && submissionData.selectedDealLoanNumbers.length > 0) {
          delete submissionData.loanNumber;
        }

        // For AUDIT CONFIRM requests, rename loanAmounts to loanNumbers
        if (requestType.name.toUpperCase() === "AUDIT CONFIRM" && submissionData.loanAmounts) {
          submissionData.loanNumbers = submissionData.loanAmounts.map(la => la.loanNumber);
          delete submissionData.loanAmounts;
        }

        if (submissionData.loanAmounts) {
          submissionData.amount = submissionData.loanAmounts.reduce((sum, la) => sum + Number(la.amount || 0), 0);
        }



        let formData: FormData = new FormData();
        formData.append("templateId", request.templateId);
        formData.append("locations", request.locations);
        formData.append("name", request.name);
        formData.append("referenceName", request.referenceName);
        formData.append(
          "requestJsonData",
          this.removeProxyData(JSON.stringify(submissionData)),
        );
        formData.append("referenceType", request.referenceType);

        if (this.isEditable) formData.append("parentId", this.task.parentId);

        this.files.forEach((f) => {
          formData.append("files", f, f.name);
        });

        this.isLoading = true;

        const response = await lastValueFrom(
          this.taskDataService.insertTaskAsFormData(formData),
        );
        sendNotificationMessage(
          `${response[1].data[0].task.taskId} task created`,
          this.messageService,
        );

        this.isLoading = false;

        this.formElement.nativeElement.reset();
        this.formElement.nativeElement.submit();
      } catch (err: any) {
        this.isLoading = false;
        this.loggingService.logError(err, SeverityLevel.Error);
        notify(err.message, "error", getNotificationTimeout());
      }
    }
  }

  override async ngOnInit() {
    try {
      this.isLoading = true;

      let requestType = await lastValueFrom(
        this.taskDataService.getTaskTemplatesByType(this.taskTemplateName),
      );
      this.requestTypes = requestType[1] ?? [];

      await super.ngOnInit();

      const feeTypeData = await lastValueFrom(
        this.taskDataService.getFeeTypes(),
      );
      // Sort and format fee types in title case
      this.feeTypes = feeTypeData[1]
        .sort((a, b) => a.name.localeCompare(b.name))
        .map(feeType => ({
          ...feeType,
          name: FormUtils.toTitleCase(feeType.name)
        }));

      this.isEditable = this.task != null ? true : false;

      if (this.isEditable) {
        this.requestData = FormUtils.mapFromTask<any>(this.task, function () {
          return {};
        } as any);

        // Initialize loan numbers display for deal-based request types
        if (this.requestData.dealNumber && this.dealData) {
          const selectedDeal = this.dealData.find(deal => deal.name === this.requestData.dealNumber);
          if (selectedDeal && selectedDeal.loanNumbers) {
            this.selectedDealLoanNumbers = selectedDeal.loanNumbers;
            this.requestData.selectedDealLoanNumbers = this.selectedDealLoanNumbers;
          }
        }

        // Handle AUDIT CONFIRM case where task data might have loanNumbers instead of loanAmounts
        if (this.requestData.requestType?.toUpperCase() === "AUDIT CONFIRM" &&
            this.requestData.loanNumbers &&
            !this.requestData.loanAmounts) {
          // Convert loanNumbers array to loanAmounts format for the grid
          this.requestData.loanAmounts = this.requestData.loanNumbers.map((loanNumber: string) => ({
            key: this.generateUniqueId(),
            loanNumber: loanNumber,
            name: loanNumber,
            amount: 0, // AUDIT CONFIRM doesn't need amounts
            effectiveDate: new Date()
          }));
        }

        //remap loan numbers to add name and dealName for lookup match

        if (
          this.requestData.loanAmounts &&
          this.requestData.loanAmounts.length > 0
        ) {
          const remappedAmounts = [];

          this.requestData.loanAmounts.forEach((amount) => {
            let loan = this.loanNumbers.find((d) => d.name == amount.loanNumber);
            if (loan) {
              remappedAmounts.push({
                key: amount.key || this.generateUniqueId(),
                amount: amount.amount,
                name: amount.loanNumber,
                loanNumber: amount.loanNumber,
                dealName: loan.dealName,
                // effectiveDate: amount.effectiveDate || new Date(),
              });
            } else {
              // Fallback if loan not found
              remappedAmounts.push({
                key: amount.key || this.generateUniqueId(),
                amount: amount.amount,
                name: amount.loanNumber,
                loanNumber: amount.loanNumber,
                dealName: null,
                // effectiveDate: amount.effectiveDate || new Date(),
              });
            }
          });

          this.requestData.loanAmounts = remappedAmounts;

          this.doNotExcludeSelectedLoans = true;
        }

        // For editing, we need to programmatically trigger form configuration
        // since onRequestTypeSelectionChanged is only called on user interaction
        if (this.requestData.requestType) {
          const requestTypeObj = this.requestTypes.find(rt => rt.name === this.requestData.requestType);
          if (requestTypeObj) {
            // Simulate the selection event to create the form configuration
            this.onRequestTypeSelectionChanged({
              value: this.requestData.requestType,
              selectedItem: { name: this.requestData.requestType }
            });
          }
        }
      }

      this.isLoading = false;
    } catch (err: any) {
      this.loggingService.logError(err, SeverityLevel.Error);
      this.isLoading = false;
      notify(err.message, "error", getNotificationTimeout());
    }

    this.isUrgent = false;
    this.mandatoryFiles = false;
    this.billableToClientData = [
      { id: 1, name: "Yes" },
      { id: 2, name: "No" },
    ];
  }
}
